import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import {
  PRODUCT_TYPES,
  RAW_UNIT_SUFFIX,
  formatIqd,
  fromIqd,
  subtract,
  type ProductCategoryDto,
  type ProductDto,
  type ProductType,
  type RawItemDto,
  type RecipeLineDto,
  type TagDto,
} from '@gc/shared';

import { useAuth } from '@/features/auth/useAuth';
import { MediaUpload } from '@/features/settings/controls';
import { api, ApiError } from '@/lib/api';

/**
 * Wave 3: the API returns `trackStock` on every product; the shared DTO gains
 * the field when the lead lands it. Optional so existing fixtures compile.
 */
type ProductWithStock = ProductDto & { trackStock?: boolean };

interface ProductFormDialogProps {
  /** Null when adding. */
  product: ProductWithStock | null;
  categories: ProductCategoryDto[];
  tags: TagDto[];
  onClose: () => void;
  onSaved: () => void;
}

interface FieldError {
  field: string;
  message: string;
}

/** One recipe row as the form edits it — quantity kept as the typed string. */
export interface RecipeLineDraft {
  rawItemId: string;
  quantity: string;
}

/** Millidinars -> whole IQD for the number inputs. Money never becomes a float. */
function toIqdInput(md: string): string {
  return (BigInt(md) / 1000n).toString();
}

/**
 * What the "Track stock" checkbox should read after the type changes (R1#6).
 *
 * Until the user touches the checkbox themselves, the type owns it: SERVICE
 * unticks (a service has no shelf — tracked, it is unsellable at its
 * permanent stock 0, which is exactly the `insufficient_stock` trap this
 * exists to close), stock-bearing types tick. Once touched, the user's choice
 * stands whatever the type does — mirroring the server, where an explicit
 * `trackStock` in the body always beats the type-aware default.
 */
export function trackStockForType(
  type: ProductType,
  current: boolean,
  userTouched: boolean,
): boolean {
  if (userTouched) return current;
  return type !== 'SERVICE';
}

/**
 * Add / edit a product (SPEC §6.3).
 *
 * Form fields fixed by the spec: image, name, category, tag(s), type, barcode,
 * cost, price, opening stock. Opening stock is only settable on creation —
 * afterwards stock moves through the ⊕ adjustment so every change lands in the
 * StockMovement ledger the Stores report reads.
 */
export function ProductFormDialog({
  product,
  categories,
  tags,
  onClose,
  onSaved,
}: ProductFormDialogProps) {
  const { t } = useTranslation();
  const { venue } = useAuth();
  const currencySymbol = venue?.currencySymbol ?? 'IQD';
  const editing = product !== null;

  const [name, setName] = useState(product?.name ?? '');
  const [type, setType] = useState<ProductType>(product?.type ?? 'NUMERIC');
  const [barcode, setBarcode] = useState(product?.barcode ?? '');
  const [productCategoryId, setProductCategoryId] = useState(product?.productCategoryId ?? '');
  const [tagIds, setTagIds] = useState<string[]>(product?.tags.map((tag) => tag.id) ?? []);
  const [cost, setCost] = useState(product ? toIqdInput(product.costMd) : '0');
  const [price, setPrice] = useState(product ? toIqdInput(product.priceMd) : '0');
  const [openingBalance, setOpeningBalance] = useState('0');
  const [lowStockThreshold, setLowStockThreshold] = useState(
    product?.lowStockThreshold?.toString() ?? '',
  );
  const [isActive, setIsActive] = useState(product?.isActive ?? true);
  // Wave 3 — the single stock authority; the recipe editor rides on it.
  const [trackStock, setTrackStock] = useState(product?.trackStock ?? true);
  // Has the user themselves touched the checkbox? While they have not, the
  // TYPE drives it (R1#6): picking Service unticks tracking — a service has
  // no shelf, and a tracked service is unsellable at its permanent stock 0.
  // Editing counts as touched: the stored value is an explicit choice.
  const [trackStockTouched, setTrackStockTouched] = useState(editing);
  const [recipeLines, setRecipeLines] = useState<RecipeLineDraft[]>([]);
  const [rawItems, setRawItems] = useState<RawItemDto[]>([]);
  const [rawItemsUnavailable, setRawItemsUnavailable] = useState(false);

  /*
   * The picture (SPEC §6.3's first column).
   *
   * THREE states, not two, which is why the id starts `undefined`: a product
   * already carrying an image arrives with a URL but no asset id — the read
   * model returns a signed URL and never the id behind it. So `undefined`
   * means "staff did not touch it", and the PATCH omits the field entirely
   * rather than sending a null that would clear the image every time someone
   * corrected a price. `null` is the deliberate clear; a string is a new upload.
   */
  const [imageAssetId, setImageAssetId] = useState<string | null | undefined>(undefined);
  const [imageUrl, setImageUrl] = useState<string | null>(product?.imageUrl ?? null);

  const [errors, setErrors] = useState<FieldError[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // The raw shelf, for the recipe selects. Requires `products.manage`, which
  // whoever opened this dialog already holds.
  useEffect(() => {
    void (async () => {
      try {
        setRawItems(await api.get<RawItemDto[]>('/raw-items'));
        setRawItemsUnavailable(false);
      } catch {
        setRawItems([]);
        setRawItemsUnavailable(true);
      }
    })();
  }, []);

  // Editing: seed the editor with the stored recipe.
  useEffect(() => {
    if (!product) return;
    void (async () => {
      try {
        const lines = await api.get<RecipeLineDto[]>(`/products/${product.id}/recipe`);
        setRecipeLines(
          lines.map((line) => ({ rawItemId: line.rawItemId, quantity: String(line.quantity) })),
        );
      } catch {
        // Editor starts empty; saving would clear the recipe, so surface it.
        setErrors((current) => [
          ...current,
          { field: '(root)', message: t('cafe.products.recipeLoadFailed') },
        ]);
      }
    })();
    // The dialog is remounted per product; product.id is stable within it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  const errorFor = (field: string) => errors.find((error) => error.field === field)?.message;

  const costMd = fromIqd(Number(cost) || 0);
  const priceMd = fromIqd(Number(price) || 0);
  const marginMd = subtract(priceMd, costMd);
  const marginPercent = costMd > 0n ? (Number(marginMd) / Number(costMd)) * 100 : null;

  const inputClass =
    'w-full rounded-control border border-subtle bg-input px-3 py-2 text-content outline-none focus:border-primary';

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="max-h-full w-full max-w-2xl overflow-auto rounded-card border border-subtle bg-raised p-5 shadow-2xl"
      >
        <h2 className="mb-4 text-lg font-semibold text-content">
          {editing ? t('cafe.products.editProduct') : t('cafe.products.addNew')}
        </h2>

        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2 block">
            <span className="mb-1 block text-sm text-content-secondary">{t('common.name')}</span>
            <input
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              // Arabic product names are the norm here (السموذي, الكيك); the
              // field is plain text with no pattern so nothing mangles them.
              className={inputClass}
            />
            {errorFor('name') ? (
              <p className="mt-1 text-xs text-danger">{errorFor('name')}</p>
            ) : null}
          </label>

          <label className="block">
            <span className="mb-1 block text-sm text-content-secondary">
              {t('cafe.products.category')}
            </span>
            <select
              value={productCategoryId}
              onChange={(event) => setProductCategoryId(event.target.value)}
              className={inputClass}
            >
              <option value="">{t('cafe.products.noCategory')}</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                  {category.printerName ? ` → ${category.printerName}` : ''}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1 block text-sm text-content-secondary">
              {t('cafe.products.type')}
            </span>
            <select
              value={type}
              onChange={(event) => {
                const nextType = event.target.value as ProductType;
                setType(nextType);
                setTrackStock((current) => trackStockForType(nextType, current, trackStockTouched));
              }}
              className={inputClass}
            >
              {PRODUCT_TYPES.map((productType) => (
                <option key={productType} value={productType}>
                  {t(`cafe.products.types.${productType}`)}
                </option>
              ))}
            </select>
          </label>

          <fieldset className="col-span-2">
            <legend className="mb-1 block text-sm text-content-secondary">
              {t('cafe.products.tags')}
            </legend>
            <div className="flex flex-wrap gap-2">
              {tags.length === 0 ? (
                <p className="text-xs text-content-muted">{t('cafe.products.noTagsYet')}</p>
              ) : (
                tags.map((tag) => (
                  <label
                    key={tag.id}
                    className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors ${
                      tagIds.includes(tag.id)
                        ? 'border-primary bg-primary/10 text-content'
                        : 'border-subtle text-content-secondary hover:bg-hover'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={tagIds.includes(tag.id)}
                      onChange={() => toggleTag(tag.id)}
                      className="h-3.5 w-3.5 accent-[var(--brand-primary)]"
                    />
                    {tag.name}
                    {/* The tag's printer IS the routing (SPEC §10), so showing
                        it here is what makes the consequence of ticking
                        visible at the moment of ticking. */}
                    {tag.printerName ? (
                      <span className="text-xs text-content-muted">→ {tag.printerName}</span>
                    ) : null}
                  </label>
                ))
              )}
            </div>
            {errorFor('tagIds') ? (
              <p className="mt-1 text-xs text-danger">{errorFor('tagIds')}</p>
            ) : null}
          </fieldset>

          <label className="block">
            <span className="mb-1 block text-sm text-content-secondary">
              {t('cafe.products.barcode')}
            </span>
            <input
              value={barcode}
              onChange={(event) => setBarcode(event.target.value)}
              placeholder={t('cafe.products.barcodePlaceholder')}
              className={`${inputClass} tabular`}
            />
            {errorFor('barcode') ? (
              <p className="mt-1 text-xs text-danger">{errorFor('barcode')}</p>
            ) : null}
          </label>

          <label className="block">
            <span className="mb-1 block text-sm text-content-secondary">
              {t('cafe.products.lowStockThreshold')}
            </span>
            <input
              type="number"
              min="0"
              step="1"
              value={lowStockThreshold}
              onChange={(event) => setLowStockThreshold(event.target.value)}
              placeholder={t('cafe.products.venueDefault')}
              className={`${inputClass} tabular`}
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-sm text-content-secondary">
              {t('cafe.products.cost')}
            </span>
            <input
              type="number"
              min="0"
              step="1"
              value={cost}
              onChange={(event) => setCost(event.target.value)}
              className={`${inputClass} tabular`}
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-sm text-content-secondary">
              {t('cafe.products.price')}
            </span>
            <input
              type="number"
              min="0"
              step="1"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
              className={`${inputClass} tabular`}
            />
            {errorFor('price') ? (
              <p className="mt-1 text-xs text-danger">{errorFor('price')}</p>
            ) : null}
          </label>

          {/* Live margin, so a mistyped cost is obvious before saving. */}
          <p className="col-span-2 text-sm text-content-secondary">
            {t('cafe.products.marginPreview', {
              amount: formatIqd(marginMd, { currencySymbol }),
              percent: marginPercent === null ? '—' : marginPercent.toFixed(1),
            })}
          </p>

          {editing ? (
            <p className="col-span-2 rounded-control border border-subtle bg-input px-3 py-2 text-xs text-content-muted">
              {t('cafe.products.stockIsLedgered', {
                stock: product.stockQuantity,
                opening: product.openingBalance,
              })}
            </p>
          ) : (
            <label className="block">
              <span className="mb-1 block text-sm text-content-secondary">
                {t('cafe.products.openingBalance')}
              </span>
              <input
                type="number"
                min="0"
                step="1"
                value={openingBalance}
                onChange={(event) => setOpeningBalance(event.target.value)}
                className={`${inputClass} tabular`}
              />
            </label>
          )}

          <StockAndRecipeSection
            trackStock={trackStock}
            onTrackStock={(value) => {
              // A hand on the checkbox is an explicit decision; type switches
              // stop second-guessing it from here on.
              setTrackStockTouched(true);
              setTrackStock(value);
            }}
            rawItems={rawItems}
            rawItemsUnavailable={rawItemsUnavailable}
            lines={recipeLines}
            onLines={setRecipeLines}
          />

          <label className="col-span-2 flex items-center gap-2 rounded-control border border-subtle bg-input px-3 py-2">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(event) => setIsActive(event.target.checked)}
              className="h-4 w-4 accent-[var(--brand-primary)]"
            />
            <span className="text-sm text-content">{t('cafe.products.onTheMenu')}</span>
          </label>

          {/*
            The picture the products table and the cafe order tiles already
            render. The column, the API and this control all existed; only the
            wiring was missing, which is why the field sat here disabled.
          */}
          <div className="col-span-2">
            <MediaUpload
              label={t('common.image')}
              kind="PRODUCT_IMAGE"
              currentUrl={imageUrl}
              onUploaded={(assetId, url) => {
                setImageAssetId(assetId);
                setImageUrl(url);
              }}
              onCleared={() => {
                setImageAssetId(null);
                setImageUrl(null);
              }}
            />
          </div>
        </div>

        {errorFor('(root)') ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {errorFor('(root)')}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:opacity-60"
          >
            {t('common.save')}
          </button>
        </div>
      </form>
    </div>
  );

  function toggleTag(tagId: string): void {
    setTagIds((current) =>
      current.includes(tagId) ? current.filter((id) => id !== tagId) : [...current, tagId],
    );
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setErrors([]);
    setSubmitting(true);

    const body = {
      name,
      type,
      barcode: barcode.trim() || null,
      productCategoryId: productCategoryId || null,
      tagIds,
      cost: Number(cost) || 0,
      price: Number(price) || 0,
      lowStockThreshold: lowStockThreshold === '' ? null : Number(lowStockThreshold),
      trackStock,
      isActive,
      // Omitted when untouched — the update schema reads an absent field as
      // "leave it alone" and a present null as "clear it".
      ...(imageAssetId === undefined ? {} : { imageMediaAssetId: imageAssetId }),
    };

    try {
      let savedId: string;
      if (editing) {
        await api.patch(`/products/${product.id}`, body);
        savedId = product.id;
      } else {
        const created = await api.post<ProductDto>('/products', {
          ...body,
          openingBalance: Number(openingBalance) || 0,
        });
        savedId = created.id;
      }

      // The recipe is its own atomic PUT (replace-whole, empty clears). Only a
      // tracked product's recipe is editable here; unticking leaves any stored
      // recipe alone — it is inert while trackStock is off.
      if (trackStock) {
        await api.put(`/products/${savedId}/recipe`, {
          lines: recipeLines
            .filter((line) => line.rawItemId && Number(line.quantity) > 0)
            .map((line) => ({
              rawItemId: line.rawItemId,
              quantity: Math.round(Number(line.quantity)),
            })),
        });
      }

      onSaved();
      onClose();
    } catch (caught) {
      if (caught instanceof ApiError && Array.isArray(caught.details)) {
        setErrors(caught.details as FieldError[]);
      } else {
        setErrors([
          {
            field: '(root)',
            message: caught instanceof Error ? caught.message : t('cafe.products.couldNotSave'),
          },
        ]);
      }
    } finally {
      setSubmitting(false);
    }
  }
}

/**
 * The "Track stock" checkbox and, while it is ticked, the recipe editor
 * (Wave 3, #17). One exported component so the rule the tests pin — the
 * checkbox DRIVES the recipe section — lives in one place.
 *
 * The hint line spells out the fallback: a tracked product with no recipe
 * lines counts its own stock, exactly as before this wave.
 */
export function StockAndRecipeSection({
  trackStock,
  onTrackStock,
  rawItems,
  rawItemsUnavailable,
  lines,
  onLines,
}: {
  trackStock: boolean;
  onTrackStock: (value: boolean) => void;
  rawItems: readonly RawItemDto[];
  rawItemsUnavailable: boolean;
  lines: readonly RecipeLineDraft[];
  onLines: (lines: RecipeLineDraft[]) => void;
}) {
  const { t } = useTranslation();

  const inputClass =
    'rounded-control border border-subtle bg-input px-2 py-1.5 text-sm text-content outline-none focus:border-primary';

  const unitFor = (rawItemId: string): string => {
    const rawItem = rawItems.find((item) => item.id === rawItemId);
    return rawItem ? RAW_UNIT_SUFFIX[rawItem.unit] : '';
  };

  const setLine = (index: number, patch: Partial<RecipeLineDraft>): void => {
    onLines(lines.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  };

  return (
    <div className="col-span-2 rounded-control border border-subtle bg-input px-3 py-2">
      {/* Presented in the owner's own words — a "No stock" switch. Ticked
          means the item is never counted and an order can never be refused
          as out of stock; underneath it is the same trackStock flag the
          server has always had, just read in the direction the owner thinks
          in ("add an option that says 'no stock'"). */}
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={!trackStock}
          onChange={(event) => onTrackStock(!event.target.checked)}
          className="h-4 w-4 accent-[var(--brand-primary)]"
        />
        <span className="text-sm text-content">{t('cafe.products.trackStock')}</span>
      </label>
      <p className="mt-1 text-xs text-content-muted">{t('cafe.products.trackStockHint')}</p>

      {trackStock ? (
        <div data-recipe-editor className="mt-3 border-t border-subtle pt-3">
          <div className="mb-1 flex items-center gap-2">
            <span className="text-sm font-medium text-content">{t('cafe.products.recipe')}</span>
          </div>
          <p className="mb-2 text-xs text-content-muted">{t('cafe.products.recipeHint')}</p>

          {rawItemsUnavailable ? (
            <p className="text-xs text-content-muted">{t('cafe.products.recipeUnavailable')}</p>
          ) : rawItems.length === 0 ? (
            <p className="text-xs text-content-muted">{t('cafe.products.noRawItemsYet')}</p>
          ) : (
            <>
              {lines.map((line, index) => (
                <div key={index} className="mb-2 flex items-center gap-2">
                  <select
                    value={line.rawItemId}
                    onChange={(event) => setLine(index, { rawItemId: event.target.value })}
                    aria-label={t('cafe.products.recipeRawItem')}
                    className={`${inputClass} flex-1`}
                  >
                    <option value="">{t('cafe.products.recipeChooseRawItem')}</option>
                    {rawItems.map((rawItem) => (
                      <option
                        key={rawItem.id}
                        value={rawItem.id}
                        // One line per ingredient — the recipe's own unique.
                        disabled={lines.some(
                          (other, otherIndex) =>
                            otherIndex !== index && other.rawItemId === rawItem.id,
                        )}
                      >
                        {rawItem.name} ({RAW_UNIT_SUFFIX[rawItem.unit]})
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={line.quantity}
                    onChange={(event) => setLine(index, { quantity: event.target.value })}
                    aria-label={t('cafe.products.recipeQuantity')}
                    className={`${inputClass} tabular w-24`}
                  />
                  <span className="w-8 text-xs text-content-muted">{unitFor(line.rawItemId)}</span>
                  <button
                    type="button"
                    onClick={() => onLines(lines.filter((_, i) => i !== index))}
                    aria-label={t('cafe.products.recipeRemoveLine')}
                    title={t('cafe.products.recipeRemoveLine')}
                    className="grid h-7 w-7 place-items-center rounded-control border border-subtle text-content-secondary hover:bg-hover hover:text-danger"
                  >
                    <X className="h-3.5 w-3.5" aria-hidden />
                  </button>
                </div>
              ))}

              <button
                type="button"
                onClick={() => onLines([...lines, { rawItemId: '', quantity: '1' }])}
                disabled={lines.length >= rawItems.length}
                className="inline-flex items-center gap-1 rounded-full border border-subtle px-3 py-1 text-xs text-content-secondary hover:bg-hover disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Plus className="h-3.5 w-3.5" aria-hidden />
                {t('cafe.products.recipeAddLine')}
              </button>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
